import { Injectable } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { Router } from '@angular/router';
import { BehaviorSubject, firstValueFrom, Observable, of, tap } from 'rxjs';
import { ROUTES } from '../shared/constants';
import { IForgotPassword, IResetPassword, IUserLogin, IVerifyToken } from '../shared/interfaces/IAuth';
import { IUserInfo } from '../shared/interfaces/IUser';
import { HttpService } from './http.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  public accessToken: string | null;
  public refreshToken: string | null;
  public userInfo: IUserInfo | null;
  private isRefreshingToken = false;
  private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  private tokenKey = 'gfliId';
  private comIdKey = 'gfliComId';

  constructor(
    private httpService: HttpService,
    private router: Router
  ) {
    // Load tokens from localStorage on initialization
    const token = localStorage.getItem(this.tokenKey);
    const refreshToken = token?.slice(0, 64);
    const accessToken = token?.slice(64);
    if (refreshToken && accessToken) {
      this.refreshToken = refreshToken;
      this.accessToken = accessToken;
    }
  }

  setUserInfo(): Observable<IUserInfo | null> {
    if (this.userInfo) return of(this.userInfo);
    return this.httpService.get('user/info');
  }

  setToken(accessToken: string, refreshToken?: string, encryptedCompanyId?: string): void {
    if (accessToken && refreshToken) {
      this.accessToken = accessToken;
      this.refreshToken = refreshToken;
      localStorage.setItem(this.tokenKey, `${refreshToken}${accessToken}`);
    }
    if (encryptedCompanyId) {
      localStorage.setItem(this.comIdKey, encryptedCompanyId);
    }
  }

  getEncryptedCompanyId(): string | null {
    return localStorage.getItem(this.comIdKey);
  }

  public clearTokens(): void {
    this.accessToken = null;
    this.refreshToken = null;
    localStorage.removeItem(this.tokenKey);
    localStorage.removeItem(this.comIdKey);
  }

  async isAuthenticated(): Promise<boolean> {
    if (!this.accessToken) return false;
    this.userInfo = await firstValueFrom(this.setUserInfo());
    return !!this.userInfo;
  }

  getRefreshToken() {
    if (!this.refreshToken) {
      throw new Error('No refresh token available');
    }

    if (this.isRefreshingToken) {
      // If refresh is already in progress, wait for the result
      return new Observable(observer => {
        this.refreshTokenSubject.subscribe({
          next: (token) => {
            if (token) {
              observer.next(token);
              observer.complete();
            }
          },
          error: (err) => {
            observer.error(err);
          }
        });
      });
    } else {
      // Start new refresh
      this.isRefreshingToken = true;
      this.refreshTokenSubject.next(null);

      return this.httpService.post('auth/refresh-token', { refreshToken: this.refreshToken }).pipe(
        // Using tap to handle side effects without affecting the stream
        tap({
          next: (token) => {
            this.isRefreshingToken = false;
            this.refreshTokenSubject.next(token);
          },
          error: (err) => {
            this.isRefreshingToken = false;
            this.refreshTokenSubject.error(err);
            // Reset subject for next attempt
            this.refreshTokenSubject = new BehaviorSubject<any>(null);
          }
        })
      );
    }
  }

  login(payload: IUserLogin) {
    return this.httpService.post('auth/login', payload);
  }

  register(payload: IUserInfo, redirectUrl?: string) {
    let params: HttpParams | undefined;
    if (redirectUrl) {
      params = new HttpParams().set('redirectUrl', redirectUrl);
    }
    return this.httpService.post('auth/register', payload, params ? { params } : undefined);
  }

  async logout(apiCall = true) {
    this.clearTokens();
    this.userInfo = null;
    this.router.navigate([ROUTES.USER.LOGIN]);
    try {
      if (this.refreshToken && apiCall) {
        await firstValueFrom(this.httpService.post('auth/logout', { refreshToken: this.refreshToken }));
      }
    } catch (error) {
      console.error(error);
    }
  }

  resetPassword(payload: IResetPassword) {
    return this.httpService.post('auth/reset-password', payload);
  }

  forgotPassword(payload: IForgotPassword) {
    return this.httpService.post('auth/forgot-password', payload);
  }

  async verifyToken(payload: IVerifyToken): Promise<boolean> {
    return await firstValueFrom(this.httpService.post('auth/verify-token', payload));
  }
}
